import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { Separator } from '@/components/ui/separator'
import { formatCFA } from '@/lib/utils'
import type {
  AgingBarDatum,
  BillsAgingResponse,
  BucketLabel,
} from '@/types/backend'
import { ResponsiveBar } from '@nivo/bar'
import { format } from 'date-fns'
import React, { type Dispatch, type SetStateAction } from 'react'

const BUCKET_LABELS_FR: Record<BucketLabel, string> = {
  Current: 'À l’échéance',
  '1-30': '1–30 jours',
  '31-60': '31–60 jours',
  '61-90': '61–90 jours',
  '90+': '90+ jours',
}

type BilanAgingProps = {
  aging?: BillsAgingResponse
  asOf: string
  setAsOf: Dispatch<SetStateAction<string>>
  gridColor: string
  gridOpacity: number
  agingBarData: AgingBarDatum[]
  agingBarKeys: BucketLabel[]
  agingColors: string[]
  customersByBucket?: Partial<
    Record<BucketLabel, { customerId: string; name: string; amount: number }[]>
  >
}

const BilanAging: React.FC<BilanAgingProps> = ({
  aging,
  asOf,
  setAsOf,
  gridColor,
  gridOpacity,
  agingBarData,
  agingBarKeys,
  agingColors,
  customersByBucket,
}) => {
  return (
    <Card className='min-h-[420px]'>
      <CardHeader>
        <div className='flex justify-between items-center'>
          <div className='flex flex-col'>
            <h4 className='text-lg font-medium'>Aging des créances</h4>
            <div className='text-sm text-muted-foreground'>
              À date au <b>{aging?.asOf ?? '—'}</b> — Terme:{' '}
              <b>{aging?.terms ?? 30} jours</b>
            </div>
          </div>

          <div className='flex flex-col'>
            <input
              type='date'
              className='h-9 rounded-md border px-2 text-sm'
              value={asOf}
              onChange={(e) => setAsOf(e.target.value)}
            />
            <Button
              variant='ghost'
              className='text-sm'
              onClick={() => setAsOf(format(new Date(), 'yyyy-MM-dd'))}
            >
              Aujourd’hui
            </Button>
          </div>
        </div>
      </CardHeader>

      <CardContent className='space-y-3'>
        {/* Bar empilé (totaux par tranche) */}
        <div className='h-[260px]'>
          <ResponsiveBar<AgingBarDatum>
            data={
              agingBarData.length ? agingBarData : [{ tranche: 'Échéancier' }]
            }
            keys={agingBarKeys}
            indexBy='tranche'
            margin={{ top: 10, right: 10, bottom: 80, left: 90 }}
            layout='vertical'
            padding={0.4}
            borderRadius={6}
            axisBottom={{ tickPadding: -20 }}
            axisLeft={{ tickPadding: 6 }}
            enableGridX
            enableGridY={false}
            theme={{
              grid: { line: { stroke: gridColor, strokeOpacity: gridOpacity } },
              tooltip: { container: { fontSize: 12, borderRadius: 8 } },
            }}
            colors={agingColors}
            enableLabel={false}
            tooltip={({ id, value }) => (
              <div className='rounded-md dark:bg-primary border bg-white p-2 text-xs shadow'>
                <div>
                  {BUCKET_LABELS_FR[String(id) as BucketLabel] ?? String(id)}:{' '}
                  <b>{formatCFA(Number(value))}</b>
                </div>
              </div>
            )}
            legends={[
              {
                anchor: 'bottom',
                direction: 'row',
                translateY: 30,
                itemWidth: 100,
                itemHeight: 14,
                itemsSpacing: 12,
                symbolSize: 12,
                dataFrom: 'keys',
              },
            ]}
          />
        </div>

        <Separator />

        {/* KPI total */}
        <div className='text-sm text-muted-foreground'>
          Total ouvert: <b>{formatCFA(aging?.grandTotalBrut ?? 0)}</b>
        </div>

        {/* Détail par tranche (clients) */}
        <Accordion type='multiple' className='w-full'>
          {agingBarKeys.map((key) => {
            const bucket = aging?.buckets?.find((b) => b.label === key)
            if (!bucket || !bucket.total) return null

            // On affiche les clients par tranche si fournis, sinon rien
            const customers = customersByBucket?.[key] ?? []

            return (
              <AccordionItem key={key} value={key}>
                <AccordionTrigger>
                  <div className='flex w-full items-center justify-between'>
                    <span>{BUCKET_LABELS_FR[key]}</span>
                    <span className='text-sm text-muted-foreground'>
                      Total: <b>{formatCFA(bucket.total)}</b>
                    </span>
                  </div>
                </AccordionTrigger>
                <AccordionContent>
                  {customers.length ? (
                    <div className='space-y-1'>
                      {customers.map((c) => (
                        <div
                          key={c.customerId}
                          className='flex items-center justify-between text-sm'
                        >
                          <span className='truncate'>{c.name}</span>
                          <span className='font-medium'>
                            {formatCFA(c.amount)}
                          </span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className='text-sm text-muted-foreground'>
                      Aucun client dans cette tranche.
                    </div>
                  )}
                </AccordionContent>
              </AccordionItem>
            )
          })}
        </Accordion>
      </CardContent>
    </Card>
  )
}

export default BilanAging
